import { captureHandledException } from '@/lib/sentry';
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { AuthorData, getAuthors, saveAuthor, updateAuthor, deleteAuthor } from '@/lib/firebase';
import { useAuth } from '@/contexts/use-auth';
import { Users, Plus, Edit, Trash2, User, Award, FileText } from 'lucide-react';
import { ImageUpload } from '@/components/image-upload';
export default function AuthorsPage() {
  const [authors, setAuthors] = useState<AuthorData[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState<AuthorData | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    profilePicUrl: '',
    story: '',
    qualifications: ''
  });
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (user) {
      loadAuthors();
    }
  }, [user]);

  const loadAuthors = async () => {
    try {
      setLoading(true);
      const authorsData = await getAuthors();
      setAuthors(authorsData);
    } catch (error: any) {
      captureHandledException(error, "authors.load");
      toast({
        title: "Failed to load contributors",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddAuthor = () => {
    setEditingAuthor(null);
    setFormData({
      name: '',
      profilePicUrl: '',
      story: '',
      qualifications: ''
    });
    setModalOpen(true);
  };

  const handleEditAuthor = (author: AuthorData) => {
    setEditingAuthor(author);
    setFormData({
      name: author.name,
      profilePicUrl: author.profilePicUrl,
      story: author.story,
      qualifications: author.qualifications
    });
    setModalOpen(true);
  };

  const handleSaveAuthor = async () => {
    if (!formData.name.trim()) {
      toast({
        title: "Name required",
        description: "Please enter the contributor's name.",
        variant: "destructive",
      });
      return;
    }

    try {
      setSaving(true);
      
      if (editingAuthor) {
        await updateAuthor(editingAuthor.id!, formData);
        toast({
          title: "Contributor updated",
          description: `${formData.name} has been updated successfully. All related content records have been updated as well.`,
        });
      } else {
        await saveAuthor(formData);
        toast({
          title: "Contributor created",
          description: `${formData.name} has been added successfully.`,
        });
      }
      
      setModalOpen(false);
      loadAuthors();
    } catch (error: any) {
      captureHandledException(error, "authors.save");
      toast({
        title: editingAuthor ? "Failed to update contributor" : "Failed to create contributor",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAuthor = async (author: AuthorData) => {
    if (!confirm(`Are you sure you want to delete ${author.name}? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteAuthor(author.id!);
      toast({
        title: "Contributor deleted",
        description: `${author.name} has been deleted successfully.`,
      });
      loadAuthors();
    } catch (error: any) {
      captureHandledException(error, "authors.delete");
      toast({
        title: "Failed to delete contributor",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="flex-1 p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <Users className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Contributors</h1>
              <p className="text-slate-600">Manage content contributors and their profiles</p>
            </div>
          </div>
          <Button onClick={handleAddAuthor} className="flex items-center space-x-2">
            <Plus className="w-4 h-4" />
            <span>Add Contributor</span>
          </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-600">Total Contributors</p>
                  <p className="text-2xl font-bold text-slate-900">{authors.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <User className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-600">With Profile Pictures</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {authors.filter(a => a.profilePicUrl).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FileText className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-600">With Stories</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {authors.filter(a => a.story).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Award className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-600">With Qualifications</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {authors.filter(a => a.qualifications).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
      </div>

      {/* Authors List */}
      <Card>
          <CardHeader>
            <CardTitle>All Contributors</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full"></div>
              </div>
            ) : authors.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No contributors yet</h3>
                <p className="text-slate-600 mb-4">Get started by adding your first contributor.</p>
                <Button onClick={handleAddAuthor} className="flex items-center space-x-2">
                  <Plus className="w-4 h-4" />
                  <span>Add Contributor</span>
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {authors.map((author) => (
                  <div key={author.id} className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      {author.profilePicUrl ? (
                        <img
                          src={author.profilePicUrl}
                          alt={author.name}
                          className="w-16 h-16 rounded-full object-cover bg-slate-100"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
                          <User className="w-8 h-8 text-slate-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900 mb-1">{author.name}</h3>
                        {author.story && (
                          <p className="text-sm text-slate-600 mb-2 line-clamp-2">
                            {author.story}
                          </p>
                        )}
                        {author.qualifications && (
                          <Badge variant="secondary" className="text-xs">
                            {author.qualifications}
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-end space-x-2 mt-4 pt-4 border-t border-slate-100">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditAuthor(author)}
                        className="flex items-center space-x-1"
                      >
                        <Edit className="w-3 h-3" />
                        <span>Edit</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteAuthor(author)}
                        className="flex items-center space-x-1 text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <Trash2 className="w-3 h-3" />
                        <span>Delete</span>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
      </Card>

      {/* Add/Edit Author Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingAuthor ? 'Edit Contributor' : 'Add New Contributor'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              <div>
                <Label htmlFor="name" className="text-sm font-medium text-slate-700">
                  Name *
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter contributor's full name"
                  className="mt-1"
                />
              </div>

              <ImageUpload
                value={formData.profilePicUrl}
                onChange={(url) => handleInputChange('profilePicUrl', url)}
                label="Profile Picture"
                authorId={editingAuthor?.id}
              />

              <div>
                <Label htmlFor="story" className="text-sm font-medium text-slate-700">
                  Story
                </Label>
                <Textarea
                  id="story"
                  value={formData.story}
                  onChange={(e) => handleInputChange('story', e.target.value)}
                  placeholder="Tell the contributor's story, background, and experience..."
                  rows={4}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="qualifications" className="text-sm font-medium text-slate-700">
                  Qualifications
                </Label>
                <Input
                  id="qualifications"
                  value={formData.qualifications}
                  onChange={(e) => handleInputChange('qualifications', e.target.value)}
                  placeholder="e.g., PhD in Psychology, Licensed Therapist, Certified Wellness Coach"
                  className="mt-1"
                />
              </div>

              <div className="flex justify-end space-x-4 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setModalOpen(false)}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveAuthor}
                  disabled={saving}
                  className="flex items-center space-x-2"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>{editingAuthor ? 'Updating...' : 'Creating...'}</span>
                    </>
                  ) : (
                    <>
                      <span>{editingAuthor ? 'Update Contributor' : 'Create Contributor'}</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
      </Dialog>
    </div>
  );
}